import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { ModuleTile } from '@platform/ui';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { catalogLabel, moduleNav, navGroup, navGroups } from '@platform/navigation';
import { useAppSelector } from '@platform/state/hooks';
import { selectUi } from '@platform/state/selectors';
import { appRoutes } from '../../shell/appRoutes';
import { useWorkspaceCatalog } from '../../shell/useWorkspaceCatalog';
import { areaModules } from './areaModules';
import type { IAreaMenuProps } from './IAreaMenuProps';
import { useAreaStats } from './useAreaStats';
import '@platform/engine/menu/menu.css';
import './area.css';

/**
 * Ліве меню огляду напряму (прототип v4 G:L346–371): «Напрям / назва», «Огляд» (активний), «Модулі» — плитка 18px, назва, кількість
 * відкритих; «Інші напрями» — групи верхнього меню, що мають модулі в просторі (поточна — активна).
 */
export function AreaMenu({ ws, group }: IAreaMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { modules, stats } = useAreaStats(ws, group);
  const { catalog, parliament } = useWorkspaceCatalog(ws);
  // «Інші напрями» — групи, що мають модулі в просторі (прототип arAllCats); поточна — завжди
  const areas = navGroups().filter((g) => g.key === group || areaModules(catalog, g.key, label, undefined, parliament).length > 0);
  const collapsed = useAppSelector((s) => selectUi(s).leftMenuCollapsed);
  const def = navGroup(group);
  const title = def ? label(catalogLabel(def)) : group;
  return (
    <aside className={cx('po-menu mnav', collapsed && 'is-collapsed')} aria-label={t('areaPage.menu')}>
      <nav className="po-menu__nav mnav__nav" aria-label={t('areaPage.menu')}>
        <div className="mnav__head"><span className="mnav__eyebrow">{t('areaPage.area')}</span><span className="mnav__title">{title}</span></div>
        <span className="mnav__item ar-mitem is-active" aria-current="page">{t('areaPage.overview')}</span>
        <div className="ar-mhead">{t('areaPage.modules')}</div>
        {modules.map((m) => {
          const nav = moduleNav(m.key);
          return (
            <Link key={m.key} className="mnav__item ar-mmod" to={routes.module(m.key)}>
              <ModuleTile icon={nav.icon} tone={nav.tone} size={18} />
              <span className="mnav__label">{m.name}</span>
              <span className="mnav__count">{stats?.modules.find((c) => c.key === m.key)?.open ?? ''}</span>
            </Link>
          );
        })}
        <div className="ar-mhead">{t('areaPage.otherAreas')}</div>
        {areas.map((g) => (
          <Link key={g.key} className={cx('mnav__item ar-marea', g.key === group && 'is-active')} to={appRoutes.area(g.key)} aria-current={g.key === group ? 'page' : undefined}>
            <span className="mnav__label">{label(catalogLabel(g))}</span>
          </Link>
        ))}
      </nav>
    </aside>
  );
}
